import { v4 as uuidv4 } from "uuid";
import { constructPathURL } from "../utils/constructPathURL.js";
import { CHAT_SESSION_ID, SOURCE_CHAT, IS_LOGIN } from "../../constants.js";
import { formatMessageData } from "../utils/formatData.js";
import { concatMessage, setLoadingReply, setMessages } from "../features/messagesSlice.js";
import store from "../app/store.js";
import { resetSelfService, setSelfService } from "../features/sessionSlice.js";
import intentCopywriting from "../utils/intentCopywriting.js"
import { setIsTypingContainerState } from "../features/componentStateSlice.js";

export async function requestChat() {
    const requestChatUrl = constructPathURL("/v2/marina/initiateChat");
    const deviceId = uuidv4();
    const timeNow = new Date();
    const expiredAt = new Date(timeNow.getTime() + 6 * 60000);

    const requestData = {
        "channel": "WEB",
        "deviceId": deviceId,
        "clientId": "1da218fb588753921da37d1b10ad0551",
    };

    const response = await fetch(requestChatUrl, {
        method: "POST",
        headers: {
            "Content-Type": "application/json"
        },
        body: JSON.stringify(requestData)
    });

    if (!response.ok) {
        throw new Error("Request response was not ok");
    }

    const responseData = await response.json();

    if (responseData.code !== "0000") {
        throw new Error("Request response was not success", responseData.message);
    } else {
        return { ...responseData, deviceId: requestData.deviceId, expiredAt };
    }
}

export async function generatePreLoginToken(correlationId) {
    const tokenURL = constructPathURL("/marina/token");

    const tokenData = {
        "client_id": "1da218fb588753921da37d1b10ad0551",
        "channel": SOURCE_CHAT,
    };

    const response = await fetch(tokenURL, {
        method: "POST", headers: {
            "Content-Type": "application/json",
            "x-correlation-id": correlationId
        }, body: JSON.stringify(tokenData)

    });

    if (!response.ok) {
        if (response.status === 401) {
            await requestChat();
        } else {
            throw new Error("Generate token response was not ok");
        }
    }
    return response.json();
}

export async function generatePostLoginToken(correlationId) {
    const tokenURL = constructPathURL("/postauth/marina/token");

    const tokenData = {
        "client_id": "1da218fb588753921da37d1b10ad0551",
        "channel": "WEB",
        "chat_id": CHAT_SESSION_ID,
        "client_secret": "3234",
        "sessionId": "43432434324",
    };

    const response = await fetch(tokenURL, {
        method: "POST", headers: {
            "Content-Type": "application/json",
            "x-correlation-id": correlationId
        }, body: JSON.stringify(tokenData)

    });

    if (!response.ok) {
        if (response.status === 401) {
            await requestChat();
        } else {
            throw new Error("Generate token response was not ok");
        }
    }
    const responseData = await response.json();
    return responseData;
}

export async function sendChat(deviceId, token, sKey, content, position, dispatch) {
    dispatch(setIsTypingContainerState(true));
    const sendChatUrl = constructPathURL("/marina/api/v2/chat");

    const sendData = {
        "content": typeof content === "string" ? content : content.accountNumber,
        "channel": "WEB",
        "chat_id": CHAT_SESSION_ID,
        "chat_type": "text",
        "request_id": uuidv4(),
        "self_service": store.getState().session.selfService,
        "selected": typeof content === "string" ? {} : content,
    };

    try {
        const sendResponse = await fetch(sendChatUrl, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
            body: JSON.stringify(sendData)
        });
        dispatch(setIsTypingContainerState(false));

        if (!sendResponse.ok) {
            // Regenerate token if token invalid
            if (sendResponse.status === 401) {
                const newRequestResponse = await requestChat();
                if (IS_LOGIN) {
                    const newTokenResponse = await generatePostLoginToken(newRequestResponse.deviceId, newRequestResponse.correlation_id);
                    await sendChat(deviceId, newTokenResponse.token, newTokenResponse.s_key, content, position, dispatch);
                } else {
                    const newTokenResponse = await generatePreLoginToken(newRequestResponse.deviceId, newRequestResponse.correlation_id);
                    await sendChat(deviceId, newTokenResponse.token, newTokenResponse.s_key, content, position, dispatch);
                }
            } else {
                throw new Error("Send response was not ok");
            }
        };

        const cloned = sendResponse.clone();
        const reader = sendResponse.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let result = "";
        let token_position = 0;
        let isStreamed = false;

        while (true) {
            const { done, value } = await reader.read();
            if (done) {
                if (IS_LOGIN) {
                    if (!isStreamed) {
                        const clonedResponse = await cloned.json();
                        if (clonedResponse.data.selection) {
                            const botReply = formatMessageData(deviceId, "bot", "Please select an account number.", position);
                            dispatch(concatMessage(botReply));
                            clonedResponse.data.selection.push({
                                "productName": "Not seeing your card? Click here.",
                                "accountNumber": "not_found",
                            });
                            const selectionMessage = formatMessageData(deviceId, "bot", clonedResponse.data.selection, position, "selection");
                            dispatch(concatMessage(selectionMessage));
                            dispatch(setSelfService(clonedResponse.data.self_service));
                            dispatch(setLoadingReply(true));
                        } else if (clonedResponse.data.data.length !== 0) {
                            const intentResponse = await intentCopywriting(clonedResponse.data.self_service.intent, clonedResponse.data.data, content);
                            const botReply = formatMessageData(deviceId, "bot", intentResponse, position);
                            dispatch(concatMessage(botReply));
                            dispatch(resetSelfService());
                        }
                    };
                }
                break
            };

            const chunk = decoder.decode(value, { stream: true });
            const chunk_obj = chunk.split("\n");
            try {
                const jsonObjects = chunk_obj.filter(item => item != "").map(item => JSON.parse(item));

                jsonObjects.forEach(jsonObject => {
                    if (jsonObject.done) {
                        dispatch(setLoadingReply(false));
                        return;
                    }

                    const currentMessages = store.getState().messages.messages;
                    let updatedMessages = [...currentMessages];

                    if (token_position == 0) {
                        const tempMessage = formatMessageData(deviceId, "bot", jsonObject.response, position);
                        updatedMessages.push(tempMessage);
                    } else {
                        const latestMessage = { ...updatedMessages[ updatedMessages.length - 1 ] };
                        latestMessage.content += jsonObject.response;
                        updatedMessages[ updatedMessages.length - 1 ] = latestMessage;
                    }
                    token_position++;
                    dispatch(setMessages(updatedMessages));
                    dispatch(resetSelfService());

                    if (jsonObject.response) {
                        result += jsonObject.response;
                        isStreamed = true;
                    }
                });
            } catch (e) {
                console.error("Error parsing JSON chunk:", e);
            }
        }
        return result;
    } catch (error) {
        throw new Error("Send response was not success", error);
    }
}

export async function endChat(token) {
    const endChatUrl = constructPathURL("/marina/api/endChat");

    const response = await fetch(endChatUrl, {
        method: "POST", headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
        }
    });

    if (!response.ok) {
        throw new Error("End chat was not successful");
    }

    return response.json();

}

export async function queue(token, chat_id) {
    const queueUrl = constructPathURL("/marina/api/queue");

    const queueData = JSON.stringify({
        chat_id,
        tenant: "Environment"
    });

    return fetch(queueUrl, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
        },
        body: queueData

    }).then(response => {
        if (!response.ok) {
            throw new Error("Queue response was not ok");
        }
        return response.json();

    }).then(result => {
        if (result.code) {
            throw new Error("Can't get queue position. Please try again.");
        }
        return result;

    }).catch(error => {
        console.error("Fetch error:", error);
        throw error;
    });
}
